import React, { useState, useEffect, useMemo } from "react";
import { CopyToClipboard } from "react-copy-to-clipboard";
import ReactMarkdown from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { materialDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import { CopyIconSuccess, CopyIcon, QuoteIcon, RetryIcon, CopyIconSuccessCircle } from "@assets/icon";
import { formatChatTime } from "@utils/index";
import type { ChatMessage } from "../../../../types/chat-agent";
import { ChatAgentType } from "@constants/chat-agents";
import { SenderType } from "../../../../types/chat-message";
import {
  extractImageUrlsFromBlockContents,
  extractTextFromTextBlocksOnly,
  isImagesBlockType,
} from "@utils/chat-message";

import ImageGallery from "./ImageGallery";
import {
  ComponentInfoChipFromAttached,
  ComponentInfoChipFromPayload,
} from "./ComponentInfoChip";
import "./ChatUser.scss";
import { useTranslation } from "react-i18next";
import type { ComponentInfoPayload } from "../../../../types/chat-message";
import type { MessageGroupPosition } from "./conversationGroupUtils";

type ChatUserProps = {
  message: string;
  time?: string;
  isRead?: boolean;
  messageId?: string;
  uploadStatus?: 'uploading' | 'success' | 'failed';
  uploadError?: string;
  fullMessage?: ChatMessage; // Full message object for retry
  onRetryUpload?: (message: ChatMessage) => void;
  onQuote?: (message: ChatMessage) => void; // Quote/Reply handler
  activeAgent?: ChatAgentType; // To check if in CSKH
  isQueuing?: boolean; // Flag for messages waiting for connection
  scrollToQuotedMessage?: (targetMessageId: string | number) => Promise<void>;
  groupPosition?: MessageGroupPosition;
};

const cleanMessageText = (text: string) =>
  text.replace(/<!--[\s\S]*?-->/g, '').trim();

// Tin nhắn user là text thô: "123. 123." không được thành <ol start="123"> lồng nhau
// (số thứ tự tràn khỏi bubble) - escape dấu chấm sau số ở đầu dòng để giữ nguyên nội dung.
const escapeOrderedListMarkers = (text: string) =>
  text.replace(/^(\s*\d+)\.(?=\s|$)/gm, '$1\\.');

const isMarkdownImagesOnly = (text: string) => {
  const cleaned = cleanMessageText(text);
  if (!cleaned) return false;
  return Boolean(cleaned) && /^(\!\[image\]\(.*?\)(\s|\\n|\\r\\n)*)+$/.test(cleaned);
};

const normalizeMarkdownHref = (href?: string): string => {
  if (!href) return '#';
  const trimmed = href.trim();
  if (/^(?:https?:\/\/|mailto:|tel:)/i.test(trimmed)) return trimmed;
  if (/^[a-zA-Z0-9-]+\.[a-zA-Z]{2,}/.test(trimmed)) return `https://${trimmed}`;
  return trimmed;
};

const renderUserMarkdownLink = ({ href, children, ...props }: any) => {
  const safeHref = normalizeMarkdownHref(href);
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation();
  };
  return (
    <a href={safeHref} target="_blank" rel="noopener noreferrer" onClick={handleClick} {...props}>
      {children}
    </a>
  );
};

const renderUserCodeBlock = ({ inline, className, children, ...props }: any) => {
  const match = /language-(\w+)/.exec(className || "");
  if (!inline && match) {
    return (
      <SyntaxHighlighter {...props} style={materialDark} language={match[1]} PreTag="div">
        {String(children ?? '').replace(/\n$/, "")}
      </SyntaxHighlighter>
    );
  }
  return <code className={className} {...props}>{children}</code>;
};

const userMarkdownComponents = {
  a: renderUserMarkdownLink,
  code: renderUserCodeBlock,
};

const ChatUser: React.FC<ChatUserProps> = ({
  message,
  time,
  uploadStatus,
  fullMessage,
  onRetryUpload,
  onQuote,
  activeAgent,
  scrollToQuotedMessage,
  groupPosition,
}) => {
  const { t } = useTranslation();
  const rawTime = time || fullMessage?.time || fullMessage?.createdAt;
  const formattedTime = formatChatTime(rawTime || new Date().toISOString());
  const shouldShowTimeTooltip = Boolean(formattedTime);
  const timeTooltipProps = shouldShowTimeTooltip
    ? {
        'data-tooltip-id': 'cskh-msg-time-tooltip',
        'data-tooltip-content': formattedTime,
        'data-tooltip-place': 'right' as const,
      }
    : {};
  const [showCopyToast, setShowCopyToast] = useState(false);

  // Auto hide toast after 2 seconds
  useEffect(() => {
    if (showCopyToast) {
      const timer = setTimeout(() => {
        setShowCopyToast(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [showCopyToast]);

  const handleCopySuccess = () => {
    setShowCopyToast(true);
  };

  const handleRetryClick = () => {
    if (fullMessage && onRetryUpload) {
      onRetryUpload(fullMessage);
    }
  };

  const handleQuoteClick = () => {
    if (fullMessage && onQuote) {
      onQuote(fullMessage);
    }
  };

  // Extract image URLs from markdown message for gallery display
  const { imageUrls, textWithoutImages } = useMemo(() => {
    if (!message) return { imageUrls: [], textWithoutImages: '' };
    // Strip HTML comments
    const cleaned = message.replace(/<!--[\s\S]*?-->/g, '').trim();
    // Extract ![image](url) patterns
    const imgRegex = /!\[image\]\((.*?)\)/g;
    const urls: string[] = [];
    let match;
    while ((match = imgRegex.exec(cleaned)) !== null) {
      urls.push(match[1]);
    }
    // Remove image markdown from text
    const remaining = cleaned.replace(/!\[image\]\(.*?\)/g, '').trim();
    return { imageUrls: urls, textWithoutImages: remaining };
  }, [message]);

  const imageUploadItems = fullMessage?.imageUploadItems;
  const hasImageUploadItems = Boolean(imageUploadItems && imageUploadItems.length > 0);

  const hasImagesBlock = useMemo(
    () => Boolean(fullMessage?.blockContents?.some((b) => isImagesBlockType(b))),
    [fullMessage?.blockContents],
  );

  const textFromTextBlocksOnly = useMemo(
    () => extractTextFromTextBlocksOnly(fullMessage?.blockContents),
    [fullMessage?.blockContents],
  );

  const blockImageUrls = useMemo(
    () => extractImageUrlsFromBlockContents(fullMessage?.blockContents),
    [fullMessage?.blockContents],
  );

  const rowGalleryUrls = useMemo(() => {
    if (!hasImagesBlock || hasImageUploadItems) return [];
    return blockImageUrls.length > 0 ? blockImageUrls : imageUrls;
  }, [hasImagesBlock, hasImageUploadItems, blockImageUrls, imageUrls]);

  const useApiImagesRowLayout =
    hasImagesBlock && !hasImageUploadItems && rowGalleryUrls.length > 0;

  const bubbleMarkdownForApiRow = useMemo(() => {
    const t = textFromTextBlocksOnly.trim();
    return t || textWithoutImages.trim();
  }, [textFromTextBlocksOnly, textWithoutImages]);

  const galleryImageUrls = hasImageUploadItems
    ? imageUploadItems!.map((item) => item.s3Url || item.localUrl)
    : imageUrls;

  const showGalleryAbove =
    (useApiImagesRowLayout && rowGalleryUrls.length > 0) ||
    (!useApiImagesRowLayout &&
      (galleryImageUrls.length > 0 || hasImageUploadItems));

  const componentInfoBlocks = useMemo(() => {
    const blocks = fullMessage?.blockContents || [];
    return blocks
      .filter((b) => b.type === 'COMPONENT_INFO' && (b.payload as ComponentInfoPayload)?.text?.trim())
      .sort((a, b) => {
        const orderA = (a.payload as ComponentInfoPayload)?.order ?? 0;
        const orderB = (b.payload as ComponentInfoPayload)?.order ?? 0;
        return orderA - orderB;
      });
  }, [fullMessage?.blockContents]);

  const hasComponentInfo =
    Boolean(fullMessage?.attachedComponent) || componentInfoBlocks.length > 0;

  const isImageOnly = useMemo(() => {
    if (hasComponentInfo && !message?.trim()) return false;
    if (fullMessage?.text && isMarkdownImagesOnly(fullMessage.text)) return true;
    if (useApiImagesRowLayout) {
      return !bubbleMarkdownForApiRow.trim() && rowGalleryUrls.length > 0;
    }
    // if (!message?.trim()) return false;
    if (fullMessage?.uploadStatus) return true;
    if (fullMessage?.imageUploadItems && fullMessage.imageUploadItems.length > 0 && !message?.trim()) return true;
    if (isMarkdownImagesOnly(message)) return true;

    const cleaned = cleanMessageText(message);
    if (!cleaned) return false;
    if (/^(https?:\/\/\S+\.(?:png|jpe?g|gif|webp)(\?\S*)?\s*)+$/i.test(cleaned)) return true;
    return false;
  }, [
    useApiImagesRowLayout,
    bubbleMarkdownForApiRow,
    rowGalleryUrls.length,
    message,
    fullMessage?.uploadStatus,
    fullMessage?.imageUploadItems,
    fullMessage?.text,
    hasComponentInfo,
  ]);

  const hasBubbleText = useMemo(() => {
    if (useApiImagesRowLayout) return Boolean(bubbleMarkdownForApiRow?.trim());
    if (galleryImageUrls.length > 0 || hasImageUploadItems) {
      return Boolean(textWithoutImages?.trim());
    }
    return Boolean(cleanMessageText(message || ''));
  }, [
    useApiImagesRowLayout,
    bubbleMarkdownForApiRow,
    galleryImageUrls.length,
    hasImageUploadItems,
    textWithoutImages,
    message,
  ]);

  // Check if any image has failed (for showing retry button)
  const hasFailedImages = hasImageUploadItems
    ? imageUploadItems!.some(item => item.status === 'failed')
    : uploadStatus === 'failed';

  // Get quoted message label if this message is a reply
  const getLocalQuotedLabel = () => {
    const quotedUser = fullMessage?.quotedMessageUser;
    if (quotedUser === SenderType.USER) return 'Bạn đã trả lời chính mình';
    if (activeAgent === ChatAgentType.CSKH) return 'Bạn đã trả lời CSKH';
    return 'Bạn đã trả lời Tendoo AI';
  };

  const getHistoryQuotedLabel = () => {
    const replyType = fullMessage?.replyToMessage?.senderType;
    if (replyType === 'USER' || replyType === SenderType.USER) {
      return 'Bạn đã trả lời chính mình';
    }
    return fullMessage?.replyToMessage ? 'Bạn đã trả lời CSKH' : 'Bạn đã trả lời';
  };

  const getQuotedLabel = () => {
    if (fullMessage?.quotedMessageId && fullMessage?.quotedMessageUser) {
      return getLocalQuotedLabel();
    }
    if (fullMessage?.replyToMessageId) {
      return getHistoryQuotedLabel();
    }
    return null;
  };

  const stripMarkdown = (md: string) => {
    if (!md) return '';

    return md
      .replace(/!\[image\]\([^)]*\)/g, '')
      .replace(/^#+\s+/gm, '')
      .replace(/\*\*([^*]+)\*\*/g, '$1')
      .replace(/__([^_]+)__/g, '$1')
      .replace(/^[ \t]*[*\-+]\s+/gm, '')
      .replace(/^[ \t]*\d+\.\s+/gm, '')
      .replace(/\*([^*\n]+)\*/g, '$1')
      .replace(/_([^_\n]+)_/g, '$1')
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      .replace(/```[\s\S]*?```/g, '')
      .replace(/`([^`]+)`/g, '$1')
      .replace(/^\s*>/gm, '')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  };

  const getUserTextForCopy = () => {
    if (useApiImagesRowLayout) {
      return bubbleMarkdownForApiRow ? stripMarkdown(bubbleMarkdownForApiRow) : '';
    }
    if (galleryImageUrls.length > 0 || hasImageUploadItems) {
      return textWithoutImages ? stripMarkdown(textWithoutImages) : '';
    }
    const cleanText = message ? cleanMessageText(message).replace(/!\[.*?\]\(.*?\)/g, '') : '';
    return cleanText ? stripMarkdown(cleanText) : '';
  };

  const copyText = getUserTextForCopy();

  const renderQuoteBtn = () => {
    if (activeAgent !== ChatAgentType.CSKH) return null;
    return (
      <button
        className="flex h-8 w-8 items-center justify-center rounded-full border-0 bg-[#F2F2F4] text-[#242428] transition-opacity hover:opacity-80"
        aria-label="Trả lời"
        type="button"
        data-tooltip-id="chatbot-tooltip"
        data-tooltip-content="Trả lời"
        onClick={handleQuoteClick}
      >
        <QuoteIcon />
      </button>
    );
  };

  const renderCopyBtn = () => (
    <CopyToClipboard text={copyText} onCopy={handleCopySuccess}>
      <button
        className="flex h-8 w-8 items-center justify-center rounded-full border-0 bg-[#F2F2F4] text-[#242428] transition-opacity hover:opacity-80 [&>svg]:h-4 [&>svg]:w-4"
        aria-label="Sao chép văn bản"
        type="button"
        data-tooltip-id="chatbot-tooltip"
        data-tooltip-content="Sao chép văn bản"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
      >
        {showCopyToast ? <CopyIconSuccess /> : <CopyIcon />}
      </button>
    </CopyToClipboard>
  );

  const renderUserTime = () => {
    if (!formattedTime) return null;
    return <span className="chat-time">{formattedTime}</span>;
  };

  const renderUserActions = () => (
    <div className="chat-user-actions flex shrink-0 items-center gap-1">
      {renderUserTime()}
      {renderQuoteBtn()}
      {renderCopyBtn()}
    </div>
  );

  // Get quoted preview text (clean, 1 line)
  const getLocalQuotedPreview = () => {
    let text = fullMessage?.quotedMessageText;
    if (!text) return '';
    if (text.trim().match(/^(\!\[image\]\(.*?\)\s*)+$/)) return 'Hình ảnh';
    if (fullMessage?.quotedMessageUser === SenderType.BOT) {
      return stripMarkdown(text);
    }
    text = text
      .replace(/\!\[image\]\(.*?\)/g, '')
      .replace(/[*_#`[\]()]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    return text || 'Hình ảnh';
  };

  const getHistoryQuotedPreview = () => {
    const replyMsg = fullMessage?.replyToMessage;
    if (!replyMsg) return '';
    if (replyMsg.senderType === SenderType.BOT) {
      return stripMarkdown(replyMsg.content || '');
    }
    if (replyMsg.content) return replyMsg.content.replace(/\n/g, ' ').trim();
    const textBlock = replyMsg.blockContents?.find(
      (b: any) => b?.type === 'TEXT' && b?.payload?.text?.trim()
    );
    if (textBlock?.payload?.text) {
      return textBlock.payload.text.replace(/\n/g, ' ').trim();
    }
    return replyMsg.fullContent ? replyMsg.fullContent.replace(/\n/g, ' ').trim() : '';
  };

  const getQuotedPreview = () => {
    if (fullMessage?.quotedMessageText) return getLocalQuotedPreview();
    if (fullMessage?.replyToMessage) return getHistoryQuotedPreview();
    return '';
  };

  const handleScrollToQuoted = () => {
    const targetId = fullMessage?.quotedMessageId || fullMessage?.replyToMessageIdStr;
    if (!targetId) return;
    if (scrollToQuotedMessage) {
      scrollToQuotedMessage(targetId);
      return;
    }
    const element =
      document.getElementById(`message-${targetId}`) ||
      document.querySelector(`[data-message-id="${targetId}"]`) ||
      document.querySelector(`[data-id="${targetId}"]`);
    if (element instanceof HTMLElement) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const quotedLabel = getQuotedLabel();
  const isReply = Boolean(quotedLabel);

  return (
    <React.Fragment>
      {showCopyToast && (
        <div className="copy-toast ">
          <div className="copy-toast-content">
            <CopyIconSuccessCircle/>
            {t('common.copySuccess')}
          </div>
        </div>
      )}
      {quotedLabel && (
        <div className="chat-user flex justify-end mt-3">
          <div className="quoted-label flex gap-2 items-center text-[12px] text-[var(--colour-neutral-base)] text-right mb-1">
            <QuoteIcon /> {quotedLabel}
          </div>
        </div>
      )}

      {quotedLabel && (
        <div className="chat-user chat-user-quoted-container flex justify-end">
          <button
            type="button"
            className="chat-user-quoted-bubble"
            onClick={handleScrollToQuoted}
          >
            <div className="chat-user-quoted-text">
              {getQuotedPreview()}
            </div>
          </button>
        </div>
      )}

      <div className={`chat-user flex justify-end${isReply ? ' chat-user--reply' : ''}`}>
        {hasFailedImages && (
          <div className="flex flex-col justify-center mb-2 mr-2">
            <button
              onClick={handleRetryClick}
              className="text-gray-500 hover:opacity-70 transition-colors"
              title="Thử lại"
            >
              <RetryIcon style={{ width: 24, height: 24, cursor: 'pointer' }} />
            </button>
          </div>
        )}

        {/* Quoted Message Display - Inside bubble with red border */}
        <div className="flex flex-col items-end max-w-[85%]">
          {shouldShowTimeTooltip && (
            <div
              className="cskh-time-tooltip cskh-user-time-tooltip sr-only"
              data-testid="cskh-user-time-tooltip"
              aria-hidden="true"
            >
              {formattedTime}
            </div>
          )}

          {showGalleryAbove && (
            <div
              className="chat-user__gallery-row mb-2 w-full flex justify-end items-end gap-2"
              {...timeTooltipProps}
            >
              <ImageGallery
                imageUrls={useApiImagesRowLayout ? rowGalleryUrls : galleryImageUrls}
                imageUploadItems={hasImageUploadItems ? imageUploadItems : undefined}
              />
            </div>
          )}

          {hasComponentInfo && (
            <div
              className="chat-user__component-row mb-2 max-w-full"
              {...timeTooltipProps}
            >
              {fullMessage?.attachedComponent ? (
                <ComponentInfoChipFromAttached item={fullMessage.attachedComponent} />
              ) : (
                componentInfoBlocks.map((block, index) => (
                  <div
                    key={`component-info-${index}`}
                    className={index < componentInfoBlocks.length - 1 ? 'mb-2' : undefined}
                  >
                    <ComponentInfoChipFromPayload payload={block.payload as ComponentInfoPayload} />
                  </div>
                ))
              )}
            </div>
          )}

          {
            (!isImageOnly && hasBubbleText) && (
            <div className="chat-user__bubble-row flex max-w-full items-center justify-end gap-2">
              {renderUserActions()}

              <div className="chat-user-bubble-container relative flex items-center">
                <div
                  className={`border-chat-bot break-words${isReply ? ' border-chat-bot--reply' : ''}${activeAgent === ChatAgentType.CSKH && groupPosition ? ` border-chat-bot--cskh-${groupPosition}` : ''}`}
                  {...timeTooltipProps}
                >
              {useApiImagesRowLayout ? (
                bubbleMarkdownForApiRow ? (
                  <ReactMarkdown
                    rehypePlugins={[rehypeRaw]}
                    remarkPlugins={[remarkGfm]}
                    components={userMarkdownComponents}
                  >
                    {escapeOrderedListMarkers(bubbleMarkdownForApiRow)}
                  </ReactMarkdown>
                ) : null
              ) : galleryImageUrls.length > 0 || hasImageUploadItems ? (
                textWithoutImages ? (
                  <ReactMarkdown
                    rehypePlugins={[rehypeRaw]}
                    remarkPlugins={[remarkGfm]}
                    components={userMarkdownComponents}
                  >
                    {escapeOrderedListMarkers(textWithoutImages)}
                  </ReactMarkdown>
                ) : null
              ) : (
                <ReactMarkdown
                  rehypePlugins={[rehypeRaw]}
                  remarkPlugins={[remarkGfm]}
                  components={userMarkdownComponents}
                >
                  {escapeOrderedListMarkers(message)}
                </ReactMarkdown>
              )}
                </div>
              </div>
            </div>
            )
          }

          {/* Legacy upload status for non-per-image messages - placed outside the bubble */}
          {!hasImageUploadItems && uploadStatus === 'uploading' && (
            <div className="mt-1 text-[12px] text-gray-500 flex items-center gap-1 justify-end">
              <span>Đang tải lên...</span>
            </div>
          )}
          {!hasImageUploadItems && uploadStatus === 'failed' && (
            <button
              className="flex items-center gap-1 mt-1 justify-end cursor-pointer"
              style={{ border: 'none', background: 'none', padding: 0 }}
              onClick={handleRetryClick}
              title="Thử lại"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z" fill="#E54646" />
              </svg>
              <span className="text-[12px] font-medium text-[#E54646]">Chưa thể gửi</span>
            </button>
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

export default React.memo(ChatUser, (prevProps, nextProps) => {
  return (
    prevProps.messageId === nextProps.messageId &&
    prevProps.message === nextProps.message &&
    prevProps.isRead === nextProps.isRead &&
    prevProps.time === nextProps.time &&
    prevProps.uploadStatus === nextProps.uploadStatus &&
    prevProps.uploadError === nextProps.uploadError &&
    prevProps.isQueuing === nextProps.isQueuing &&
    prevProps.fullMessage === nextProps.fullMessage &&
    prevProps.activeAgent === nextProps.activeAgent &&
    prevProps.groupPosition === nextProps.groupPosition
  );
});
